import { Box, Flex, Text, Grid, chakra } from '@chakra-ui/react';
import { MapPin } from 'lucide-react';
import { EventImage } from '@/components/common/EventImage';
import { formatDateRange } from '@/utils/dateFormat';
import { getEventJoinCta } from '@/utils/eventJoinCta';
import type { Organization } from '@/types/events';

interface NewEventsFromOrgsSectionProps {
  events: Organization[];
  sectionHeading: string;
  onEventPress: (event: Organization) => void;
}

export function NewEventsFromOrgsSection({ events, sectionHeading, onEventPress }: NewEventsFromOrgsSectionProps) {
  if (events.length === 0) return null;

  return (
    <Box as="section" my="5" animation="fadeIn 0.3s ease-in-out">
      <Text
        as="h2"
        m="0"
        mb="4"
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="bold"
        color="volyx.text"
        fontFamily="body"
        letterSpacing="wide"
      >
        {sectionHeading}
      </Text>
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap="3">
        {events.map((event) => {
          const dateLabel = formatDateRange(event.start_date ?? null, event.end_date ?? null);
          const joinCta = getEventJoinCta(event);
          return (
            <Box
              as="article"
              key={event.id}
              bg="white"
              borderRadius="24px"
              border="1px solid"
              borderColor="volyx.mediumGray"
              boxShadow="0 2px 10px rgba(0,0,0,0.08)"
              overflow="hidden"
              transition="all 0.2s"
              _hover={{ boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }}
              cursor="pointer"
              onClick={() => onEventPress(event)}
            >
              <Box position="relative" w="full" h="120px" overflow="hidden" bg="volyx.lightGray">
                <EventImage
                  stored={event.image}
                  w="full"
                  h="full"
                  objectFit="cover"
                  transition="transform 0.3s"
                  _hover={{ transform: 'scale(1.05)' }}
                />
                {dateLabel && (
                  <Box
                    position="absolute"
                    left="3"
                    top="3"
                    bg="volyx.primary"
                    color="white"
                    fontSize="xs"
                    fontWeight="semibold"
                    px="2.5"
                    py="1"
                    borderRadius="16px"
                    fontFamily="body"
                    zIndex={1}
                  >
                    {dateLabel}
                  </Box>
                )}
              </Box>

              <Flex direction="column" gap="2" px="3" pt="2.5" pb="3">
                <Box minW="0">
                  <Text
                    as="h3"
                    m="0"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="volyx.text"
                    fontFamily="body"
                    lineHeight="tight"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {event.name}
                  </Text>
                  {event.location && (
                    <Flex align="center" gap="1" mt="1">
                      <Box as={MapPin} boxSize="13px" color="volyx.textSecondary" flexShrink={0} />
                      <Text
                        as="span"
                        fontSize="xs"
                        color="volyx.textSecondary"
                        fontFamily="body"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {event.location}
                      </Text>
                    </Flex>
                  )}
                </Box>
                <chakra.button
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!joinCta.disabled) onEventPress(event);
                  }}
                  w="full"
                  py="2"
                  px="4"
                  borderRadius="16px"
                  fontSize="sm"
                  fontWeight="semibold"
                  fontFamily="body"
                  cursor={joinCta.disabled ? 'default' : 'pointer'}
                  border="none"
                  bg={
                    joinCta.tone === 'primary'
                      ? 'volyx.primary'
                      : joinCta.tone === 'applied'
                        ? 'volyx.mediumGray'
                        : 'green.500'
                  }
                  color="white"
                  _hover={
                    joinCta.disabled
                      ? {}
                      : { opacity: 0.9 }
                  }
                  transition="opacity 0.2s"
                  _focus={{ outline: 'none', boxShadow: '0 0 0 2px #1F2932' }}
                  disabled={joinCta.disabled}
                  aria-label={joinCta.label}
                >
                  {joinCta.label}
                </chakra.button>
              </Flex>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
}
